import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  /** Shown instead of the default panel, when a route wants its own treatment. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches a render crash so one broken view does not blank the whole app
 * (§F22 polish). The error is surfaced with a way forward, not a white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sentry picks this up in production when a DSN is configured (§2.1).
    console.error('render error', error, info.componentStack);
  }

  private reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="flex min-h-[50vh] items-center justify-center p-6">
        <div className="vt-surface flex max-w-[440px] flex-col items-center gap-3 p-6 text-center">
          <AlertTriangle className="size-6 text-[var(--danger)]" />
          <h1 className="text-[15px] font-semibold">This view hit an error</h1>
          <p className="text-[13px] vt-muted">
            The rest of the app is fine. Try again, and if it keeps happening, send this message to
            an admin.
          </p>
          <code className="max-h-[120px] w-full overflow-auto rounded-[6px] bg-[var(--surface-2)] p-2 text-left text-[11px]">
            {error.message}
          </code>
          <div className="flex gap-2">
            <Button variant="primary" onClick={this.reset}>
              <RotateCw />
              Try again
            </Button>
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Reload the app
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
